<script setup lang="ts">
/**
 * FeedbackTriggerButton - opens the Sentry feedback dialog from the header of a
 * Modal, SlideOutPanel or BottomSheet (#4694).
 *
 * The side-menu item is the only other trigger (#4061), and every overlay covers
 * it — so a behandelaar could not report a problem with the screen that was open,
 * which is exactly the one they wanted to report on (SCRIVIO-FE-HK). The dialog,
 * its labels and its submission path are the integration's, as for the side menu.
 *
 * Renders nothing when Sentry is not initialised, and nothing on mijn.scrivio.nl:
 * the side menu never offered a cliënt a feedback channel, and a header button is
 * not the place to start doing so.
 */
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { MessageSquare } from 'lucide-vue-next'
import { useSentryFeedbackTrigger } from '@/composables/useSentryFeedback'
import { isClientPortal } from '@/utils/domain'

const { t } = useI18n()

const trigger = ref<HTMLElement | null>(null)
const { isAvailable } = useSentryFeedbackTrigger(trigger)
</script>

<template>
  <!-- `data-autofocus-skip`: the dialog's own first control takes focus on open,
       not a button that leaves it. -->
  <button
    v-if="isAvailable && !isClientPortal"
    ref="trigger"
    type="button"
    class="overlay-header-button feedback-trigger"
    :aria-label="t('sentry.feedback.triggerLabel')"
    :title="t('sentry.feedback.triggerLabel')"
    data-autofocus-skip
    data-testid="overlay-feedback"
  >
    <MessageSquare :size="20" aria-hidden="true" />
  </button>
</template>

<style src="@/assets/styles/overlay-header-button.css"></style>

<style scoped>
/* Takes the header's free space, so it sits right-aligned beside the × rather than
   in the middle of a `space-between` row — and on its own where there is no title. */
.feedback-trigger {
  margin-left: auto;
}
</style>
